import React, { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  Menu,
  X,
  Home,
  Users,
  Layers,
  Tag,
  Gem,
  Sparkles,
  Ruler,
  Calendar,
  Scale,
  Users2,
  Package,
  LogOut,
  User,
  Image,
  FileText,
  BookOpen,
  TrendingUp,
  Settings,
  Crown,
  Film,
  Diamond,
  Weight,
  PhoneCall,
  IndianRupee,
  Library,
  ShoppingBag,
  Star,
  ListOrdered
} from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navigationGroups = [
    {
      title: 'Overview',
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        { name: 'Users', href: '/users', icon: Users },
        { name: 'Schedule Calls', href: '/schedule-calls', icon: PhoneCall },
      ]
    },
    {
      title: 'Content & Media',
      items: [
        { name: 'Banners', href: '/banners', icon: Image },
        { name: 'CMS Pages', href: '/cms-pages', icon: FileText },
        { name: 'About Us Builder', href: '/about-us-builder', icon: FileText },
        { name: 'Guides', href: '/guides', icon: BookOpen },
        { name: 'PGM Videos', href: '/pgm-videos', icon: Film },
      ]
    },
    {
      title: 'Catalog',
      items: [
        { name: 'Peak Collections', href: '/peak-collections', icon: Library },
        { name: 'Products', href: '/products', icon: Package },
        { name: 'Categories', href: '/categories', icon: Tag },
        { name: 'Metal Types', href: '/metal-types', icon: Layers },
        { name: 'Stone Types', href: '/stone-types', icon: Gem },
      ]
    },
    {
      title: 'Attributes',
      items: [
        { name: 'Carats', href: '/carats', icon: Sparkles },
        { name: 'Sizes', href: '/sizes', icon: Ruler },
        { name: 'Occasions', href: '/occasions', icon: Calendar },
        { name: 'Weights', href: '/weights', icon: Scale },
        { name: 'Genders', href: '/genders', icon: Users2 },
      ]
    },
    {
      title: 'Pricing & Promos',
      items: [
        { name: 'Price Management', href: '/price-management', icon: IndianRupee },
        { name: 'Promo Codes', href: '/promocodes', icon: Tag },
        { name: 'Diamond Categories', href: '/diamond-categories', icon: Diamond },
        { name: 'Diamond Metal Types', href: '/diamond-metal-types', icon: Layers },
      ]
    },
    {
      title: 'Orders & Members',
      items: [
        { name: 'Elite Members', href: '/elite-members', icon: Star },
        { name: 'Product Orders', href: '/product-orders', icon: ShoppingBag },
        { name: 'Order Items', href: '/order-items', icon: ListOrdered },
      ]
    },
    {
      title: 'System Settings',
      items: [
        { name: 'Elite Subscription Plan', href: '/elite-plans', icon: Crown },
        { name: 'Social Login Settings', href: '/social-login-settings', icon: Settings },
      ]
    }
  ];

  // Flat array for route matching
  const flatNavigation = navigationGroups.flatMap(group => group.items);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isCurrentPath = (path: string) => location.pathname === path;

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <Button
              variant="ghost"
              size="icon"
              className="ml-1"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </Button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
            </div>
            <nav className="mt-5 px-2 space-y-6">
              {navigationGroups.map((group, groupIdx) => (
                <div key={groupIdx} className="space-y-1">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {group.title}
                  </h3>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = isCurrentPath(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`group flex items-center px-3 py-2 text-base font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className={`mr-4 h-5 w-5 ${isActive ? 'text-blue-700' : 'text-gray-400 group-hover:text-gray-500'}`} />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              ))}
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                  <User className="h-6 w-6 text-gray-600" />
                </div>
              </div>
              <div className="ml-3">
                <p className="text-base font-medium text-gray-700">{user?.name}</p>
                <p className="text-sm font-medium text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
              </div>
              <nav className="mt-5 flex-1 px-2 space-y-6">
                {navigationGroups.map((group, groupIdx) => (
                  <div key={groupIdx} className="space-y-1">
                    <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      {group.title}
                    </h3>
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = isCurrentPath(item.href);
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            isActive
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <Icon className={`mr-3 h-5 w-5 ${isActive ? 'text-blue-700' : 'text-gray-400 group-hover:text-gray-500'}`} />
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                    <p className="text-xs font-medium text-gray-500 capitalize">{user?.role}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  className="ml-2"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-white shadow-sm">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div>

        {/* Page header */}
        <div className="bg-white shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                    {flatNavigation.find(item => isCurrentPath(item.href))?.name || 'Dashboard'}
                  </h2>
                </div>
                <div className="hidden md:flex items-center space-x-4">
                  <span className="text-sm text-gray-500">Welcome back, {user?.name}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
